import { Right } from "@/type/core/offices/offices.types";
import {
  TransactionDetail,
  TransactionStatus,
} from "@/type/core/transaction/transaction.types";
import { TransactionHelper } from "@/utils/helpers/transaction-helper";
import AnnulationCauseModal from "@components/core/modals/AnnulationCauseModal";
import ConfirmModal from "@components/core/modals/ConfirmModal";
import { ROUTES } from "@routes/Routes";
import {
  requestGetTransactionDetail,
  requestPostFinalizeTransaction,
} from "@state/transactions/TransactionEffects";
import { ActiveUser } from "@type/core/auth/auth.types";
import { Document, Signatory } from "@type/core/dashboard/dashboard.types";
import TransactionBackButton from "@views/core/extranet/transactions/details/buttons/TransactionBackButton";
import TransactionCancelButton from "@views/core/extranet/transactions/details/buttons/TransactionCancelButton";
import TransactionClassifyButton from "@views/core/extranet/transactions/details/buttons/TransactionClassifyButton";
import TransactionDeleteButton from "@views/core/extranet/transactions/details/buttons/TransactionDeleteButton";
import TransactionEditButton from "@views/core/extranet/transactions/details/buttons/TransactionEditButton";
import TransactionLaunchButton from "@views/core/extranet/transactions/details/buttons/TransactionLaunchButton";
import TransactionPrepareButton from "@views/core/extranet/transactions/details/buttons/TransactionPrepareButton";
import { Col, Row } from "antd";
import { FunctionComponent, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import TransactionFinalizeButton from "@views/core/extranet/transactions/details/buttons/TransactionFinalizeButton";
import FinalizationCauseModal from "@/components/core/modals/FinalizationCauseModal";
import { toastError, toastSuccess } from "@/utils/helpers/toast-helper";
import DateHelper from "@/utils/helpers/date-helper";
import InfoModal from "@components/core/modals/InfoModal";

interface TransactionButtonsSectionProps {
  user?: ActiveUser;
  transactionId?: string;
  transaction: TransactionDetail;
  signatories: Signatory[];
  documents: Document[];
  certificateDegradedMode: boolean;
  signatureDegradedMode: boolean;
  sealDegradedMode: boolean;
  setLoading: (loading: boolean) => void;
  officeRights?: Right[];
  className?: string;
  canTransactionBeFinalized: boolean;
}

const TransactionButtonsSection: FunctionComponent<
  TransactionButtonsSectionProps
> = ({
  user,
  transactionId,
  transaction,
  signatories,
  documents,
  certificateDegradedMode,
  signatureDegradedMode,
  sealDegradedMode,
  setLoading,
  officeRights,
  className = "",
  canTransactionBeFinalized,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isDirectSignatureModalOpen, setIsDirectSignatureModalOpen] =
    useState<boolean>(false);
  const [annulationCause, setAnnulationCause] = useState<string>("");
  const [isAnnulationCauseModalOpen, setIsAnnulationCauseModalOpen] =
    useState<boolean>(false);
  const [isFinalizationCauseModalOpen, setIsFinalizationCauseModalOpen] =
    useState<boolean>(false);
  const [disabledAfterLaunch, setDisabledAfterLaunch] =
    useState<boolean>(false);
  const prepareOrLaunchDisabled = (documents && documents.length < 1) ?? false;

  const isTransactionExpiredAndEmittedSinceMoreThen60Days: undefined | boolean =
    transaction &&
    transaction.emittedDate != undefined &&
    transaction.status === "expired" &&
    DateHelper.isTodayAfterDatePlusDays(transaction.emittedDate, 60);

  const launchTransactionCallback = (
    transactionId: string,
    direct: boolean,
  ) => {
    navigate(ROUTES.extranet.transactions.process.generate(transactionId));
    setDisabledAfterLaunch(true);
    const userSignatory = signatories.filter(
      (signatory) => signatory.email === user?.email,
    );
    if (direct && userSignatory.length && userSignatory[0].url) {
      window.open(userSignatory[0].url, "_blank");
    }
  };

  const launchTransaction = (
    relaunch: boolean = false,
    direct: boolean = false,
  ) => {
    TransactionHelper.launchTransaction(
      transaction,
      launchTransactionCallback,
      relaunch,
      direct,
    );
  };

  const refetchTransactionDetail = () => {
    if (transactionId) {
      void requestGetTransactionDetail({ id: transactionId });
    }
  };

  const updateTransaction = (status: TransactionStatus, cause: string) => {
    TransactionHelper.updateTransaction(
      transactionId ?? transaction.id,
      transaction,
      status,
      cause,
      setLoading,
      refetchTransactionDetail,
    );
  };

  const finalizeTransaction = (cause: string) => {
    if (transactionId) {
      setLoading(true);
      requestPostFinalizeTransaction({
        id: transactionId,
        dto: { reason: cause },
      })
        .then((response) => {
          if (response.ok) {
            toastSuccess(t("success.transactionDetail.finalize"));
            refetchTransactionDetail();
            setIsFinalizationCauseModalOpen(false);
          } else {
            toastError(t("error.transaction.finalize", { transactionId }));
          }
        })
        .catch(() =>
          toastError(t("error.transaction.finalize", { transactionId })),
        )
        .finally(() => setLoading(false));
    }
  };

  return (
    <div className={`app-section-transaction-signatories ${className}`}>
      <div className="d-flex align-items-center justify-content-center flex-wrap gap-32 mt-15 mb-30 py-16">
        {transaction && transaction.status === "prepared" && (
          <>
            <TransactionDeleteButton
              transactionId={transactionId}
              transaction={transaction}
              setLoading={setLoading}
              officeRights={officeRights}
              signatureRight={"SIGNATURE_COLLECTE_MODIFIER"}
              scellementRight={"SCELLEMENT_COLLECTE_MODIFIER"}
              label={t("transaction.detail.delete")}
            />
            <TransactionLaunchButton
              user={user}
              transaction={transaction}
              signatories={signatories}
              officeRights={officeRights}
              signatureRight={"SIGNATURE_COLLECTE_LANCER"}
              scellementRight={"SCELLEMENT_COLLECTE_LANCER"}
              relaunch={false}
              launchTransaction={launchTransaction}
              disabled={prepareOrLaunchDisabled}
              disabledAfterLaunch={disabledAfterLaunch}
              setDisabledAfterLaunch={setDisabledAfterLaunch}
              setIsDirectSignatureModalOpen={setIsDirectSignatureModalOpen}
              certificateDegradedMode={certificateDegradedMode}
              signatureDegradedMode={signatureDegradedMode}
              sealDegradedMode={sealDegradedMode}
            />
          </>
        )}
        {transaction && transaction.classified && (
          <Row className="w-100 justify-content-center">
            <Col span={24} lg={20} xl={16}>
              <div className="d-flex align-items-center justify-content-end flex-wrap gap-32">
                <TransactionBackButton link={false} />
              </div>
            </Col>
          </Row>
        )}
        {transaction && transaction.status === "inProgress" && (
          <Row className="w-100 justify-content-center">
            <Col span={24} lg={20} xl={16}>
              <div className="d-flex align-items-center justify-content-end flex-wrap gap-32">
                <TransactionBackButton link />
                {canTransactionBeFinalized && (
                  <TransactionFinalizeButton
                    transaction={transaction}
                    onClick={() => setIsFinalizationCauseModalOpen(true)}
                    signatureRight={"SIGNATURE_COLLECTE_CLORE"}
                    officeRights={officeRights}
                    disabled={signatureDegradedMode}
                    label={t("transaction.detail.finalize")}
                  />
                )}
                <TransactionCancelButton
                  transaction={transaction}
                  setAnnulationCause={setAnnulationCause}
                  setIsAnnulationCauseModalOpen={setIsAnnulationCauseModalOpen}
                  officeRights={officeRights}
                  signatureRight={"SIGNATURE_COLLECTE_ANNULER"}
                  scellementRight={"SCELLEMENT_COLLECTE_ANNULER"}
                  disabled={signatureDegradedMode}
                  label={t("transaction.detail.cancel")}
                />
              </div>
            </Col>
          </Row>
        )}
        {transaction &&
          (transaction.status === "refused" ||
            transaction.status === "finished" ||
            transaction.status === "canceled") &&
          !transaction.classified && (
            <Row className="w-100 justify-content-center">
              <Col span={24} lg={20} xl={16}>
                <div className="d-flex align-items-center justify-content-center flex-wrap gap-32">
                  <TransactionClassifyButton
                    transaction={transaction}
                    onClick={updateTransaction}
                    officeRights={officeRights}
                    signatureRight={"SIGNATURE_COLLECTE_CLASSER"}
                    scellementRight={"SCELLEMENT_COLLECTE_CLASSER"}
                    label={t("transaction.detail.classify")}
                  />
                </div>
              </Col>
            </Row>
          )}
        {transaction &&
          (transaction.status === "draft" ||
            transaction.status === "processing") && (
            <>
              <TransactionDeleteButton
                transactionId={transactionId}
                transaction={transaction}
                setLoading={setLoading}
                officeRights={officeRights}
                signatureRight={"SIGNATURE_BROUILLON_MODIFIER"}
                scellementRight={"SCELLEMENT_BROUILLON_MODIFIER"}
                label={t("transaction.detail.deleteDraft")}
              />
              <TransactionEditButton
                transaction={transaction}
                officeRights={officeRights}
                signatureRight={"SIGNATURE_BROUILLON_MODIFIER"}
                scellementRight={"SCELLEMENT_BROUILLON_MODIFIER"}
                label={t("transaction.detail.modify")}
                disabled={
                  transaction.type === "signature" && certificateDegradedMode
                }
              />
              <TransactionPrepareButton
                transaction={transaction}
                onClick={updateTransaction}
                officeRights={officeRights}
                signatureRight={"SIGNATURE_BROUILLON_PREPARER"}
                scellementRight={"SCELLEMENT_BROUILLON_PREPARER"}
                label={t("transaction.detail.prepare")}
                disabled={prepareOrLaunchDisabled}
              />
              <TransactionLaunchButton
                user={user}
                transaction={transaction}
                signatories={signatories}
                officeRights={officeRights}
                signatureRight={"SIGNATURE_COLLECTE_LANCER"}
                scellementRight={"SCELLEMENT_COLLECTE_LANCER"}
                relaunch={false}
                launchTransaction={launchTransaction}
                disabled={prepareOrLaunchDisabled}
                disabledAfterLaunch={disabledAfterLaunch}
                setDisabledAfterLaunch={setDisabledAfterLaunch}
                setIsDirectSignatureModalOpen={setIsDirectSignatureModalOpen}
                certificateDegradedMode={certificateDegradedMode}
                signatureDegradedMode={signatureDegradedMode}
                sealDegradedMode={sealDegradedMode}
              />
            </>
          )}

        {transaction &&
          transaction.status === "expired" &&
          !transaction.classified && (
            <div className="d-flex flex-row justify-content-center">
              {transaction.hasData &&
                transaction.universignVersion === "U1" &&
                !isTransactionExpiredAndEmittedSinceMoreThen60Days && (
                  <TransactionLaunchButton
                    user={user}
                    transaction={transaction}
                    signatories={signatories}
                    officeRights={officeRights}
                    signatureRight={"SIGNATURE_COLLECTE_LANCER"}
                    scellementRight={"SCELLEMENT_COLLECTE_LANCER"}
                    relaunch={true}
                    launchTransaction={launchTransaction}
                    disabled={prepareOrLaunchDisabled}
                    disabledAfterLaunch={disabledAfterLaunch}
                    setDisabledAfterLaunch={setDisabledAfterLaunch}
                    setIsDirectSignatureModalOpen={
                      setIsDirectSignatureModalOpen
                    }
                    certificateDegradedMode={certificateDegradedMode}
                    signatureDegradedMode={signatureDegradedMode}
                    sealDegradedMode={sealDegradedMode}
                  />
                )}
              <TransactionClassifyButton
                transaction={transaction}
                onClick={updateTransaction}
                officeRights={officeRights}
                signatureRight={"SIGNATURE_COLLECTE_CLASSER"}
                scellementRight={"SCELLEMENT_COLLECTE_CLASSER"}
                label={t("transaction.detail.classify")}
              />
            </div>
          )}

        {transaction.status === "processing" && (
          <p
            className={
              "text-danger d-flex justify-content-center align-items-center"
            }
          >
            <InfoModal
              value={"documentsUploading"}
              warning={true}
              info={false}
              className={"text-danger"}
            />
            {t("transaction.detail.processing")}
          </p>
        )}
        <FinalizationCauseModal
          visible={isFinalizationCauseModalOpen}
          onCancel={() => setIsFinalizationCauseModalOpen(false)}
          onOk={finalizeTransaction}
          title={t("transaction.detail.finalizeTransaction")}
        />
        <AnnulationCauseModal
          visible={isAnnulationCauseModalOpen}
          onOk={updateTransaction}
          onCancel={() => setIsAnnulationCauseModalOpen(false)}
          title={t("transaction.detail.cancelTransaction")}
          annulationCause={annulationCause}
          setAnnulationCause={setAnnulationCause}
        />
        <ConfirmModal
          visible={isDirectSignatureModalOpen}
          onCancel={() => {
            setIsDirectSignatureModalOpen(false);
            launchTransaction(transaction.status === "expired");
          }}
          onOk={() => {
            setIsDirectSignatureModalOpen(false);
            launchTransaction(transaction.status === "expired", true);
          }}
          title={t("transaction.detail.directSignature.title")}
          content={t(
            `transaction.detail.directSignature.${transaction.invitationMode}`,
          )}
        />
      </div>
    </div>
  );
};

export default TransactionButtonsSection;
